// Coerce a tool-call argument that should be structured JSON but arrived
// as a JSON-encoded *string*.
//
// Why this exists: render_block / canvas_add / canvas_update declare
// their `spec` and `data` params as loosely-typed (Type.Unknown) because
// the value is polymorphic (a spec object, a row array, a bare number).
// Loosely-typed object params are serialised inconsistently across model
// families: some (notably Claude) frequently emit the value double-encoded
// as a string — `spec: "{\"kind\":\"bars\"}"` — while others (e.g. GPT)
// emit the nested object directly — `spec: {kind:"bars"}`. Neither is
// wrong; the permissive schema allows both. But the tool body and the
// downstream renderer expect an object, so the string form silently fails:
// validateSpec rejects it ("spec must be a JSON object"), and render_block
// passes it through to a renderer that reads `spec?.kind` off a string and
// gets undefined ("invalid spec"). That asymmetry is why the same canvas
// prompt works on one model and fails on another.
//
// Coercing the string form back to a value at the tool boundary makes the
// tools behave identically regardless of which model called them.
//
// We only parse strings that actually look like JSON we'd want to decode
// (object, array, or a JSON scalar). A plain string that isn't JSON — a
// caption, a label, or a bare step-ref-looking identifier like "result" —
// is returned untouched so existing guards (e.g. looksLikeStepRef) still
// see the original value.
export function coerceJsonArg(value) {
    if (typeof value !== "string") return value;
    const trimmed = value.trim();
    if (!trimmed) return value;
    const first = trimmed[0];
    const jsonish =
        first === "{" ||
        first === "[" ||
        first === '"' ||
        first === "-" ||
        (first >= "0" && first <= "9") ||
        trimmed === "true" ||
        trimmed === "false" ||
        trimmed === "null";
    if (!jsonish) return value;
    try {
        return JSON.parse(trimmed);
    } catch {
        // Looked JSON-ish but didn't parse — leave it for the tool's own
        // validation to reject with a clear message.
        return value;
    }
}
